// A schedule as the server presents it: any cron the scheduler accepts (at least 15 minutes apart),
// with its zone and, from current servers, the cron itself and its next runs. The common shapes carry
// their parts so the controls can word them; anything else is `custom` and shows its cron.
type Common = {
  timezone: string;
  /** The exact 5-field cron; present from servers that present any schedule. */
  cron?: string;
  /** The next runs the server computed, ISO; never computed here. */
  nextRuns?: string[];
};
export type AppSchedulePresentation =
  | (Common & { cadence: 'daily'; time: string })
  | (Common & { cadence: 'interval'; intervalMinutes: number })
  | (Common & { cadence: 'weekly'; time: string; days: number[] })
  | (Common & { cadence: 'custom'; cron: string });

// The scheduler's floor: nothing more often than every 15 minutes is ever presented.
const MIN_INTERVAL = 15;
const obj = (value: unknown): Record<string, unknown> | null =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
const text = (value: unknown): value is string => typeof value === 'string' && value.length > 0;

function common(raw: Record<string, unknown>): Common | null {
  if (!text(raw.timezone)) return null;
  if (raw.cron !== undefined && !text(raw.cron)) return null;
  if (
    raw.nextRuns !== undefined &&
    !(
      Array.isArray(raw.nextRuns) &&
      raw.nextRuns.length <= 10 &&
      raw.nextRuns.every((at) => text(at) && Number.isFinite(Date.parse(at)))
    )
  )
    return null;
  return {
    timezone: raw.timezone,
    ...(raw.cron === undefined ? {} : { cron: raw.cron as string }),
    ...(raw.nextRuns === undefined ? {} : { nextRuns: [...(raw.nextRuns as string[])] }),
  };
}

const validDays = (value: unknown): value is number[] =>
  Array.isArray(value) &&
  value.length > 0 &&
  value.length <= 7 &&
  value.every((day) => Number.isInteger(day) && day >= 0 && day <= 6);

export function parseAppSchedulePresentation(value: unknown): AppSchedulePresentation | null {
  const raw = obj(value);
  const base = raw && common(raw);
  if (!raw || !base) return null;
  if (raw.cadence === 'daily' && text(raw.time))
    return { ...base, cadence: 'daily', time: raw.time };
  if (
    raw.cadence === 'interval' &&
    Number.isInteger(raw.intervalMinutes) &&
    (raw.intervalMinutes as number) >= MIN_INTERVAL &&
    (raw.intervalMinutes as number) <= 1440
  )
    return { ...base, cadence: 'interval', intervalMinutes: raw.intervalMinutes as number };
  if (raw.cadence === 'weekly' && text(raw.time) && validDays(raw.days))
    return { ...base, cadence: 'weekly', time: raw.time, days: [...raw.days] };
  if (raw.cadence === 'custom' && base.cron) return { ...base, cadence: 'custom', cron: base.cron };
  return null;
}
